import { defineMessages } from 'react-intl';

export const letterSenderMessages = defineMessages({
  defaultRecipientPlaceholder: {
    defaultMessage: 'Add recipients by name, organisation, location or specialty',
    id: 'lP4HJKjYHy',
    description: 'Default placeholder for the LetterSender recipient search input',
  },
  addCcRecipientsPlaceholder: {
    defaultMessage: 'Add CC recipients',
    id: 'WmEYmAyWJJ',
    description:
      'Placeholder for the CC recipient search input in the LetterSender modal',
  },
  filter: {
    defaultMessage: 'Filter',
    id: 'N/KoQMgiUu',
    description: 'Button label for filtering LetterSender search results',
  },
  clearFilters: {
    defaultMessage: 'Clear',
    id: '8FY/djIfqs',
    description: 'Button label for clearing LetterSender search result filters',
  },
  specialty: {
    defaultMessage: 'Specialty',
    id: 'fQhhurG5p+',
    description: 'Specialty filter label for LetterSender search results',
  },
  noSpecialtiesFound: {
    defaultMessage: 'No specialties found',
    id: 'iS2I3cZN9T',
    description: 'Empty state when no specialty filter options are available',
  },
  showingResults: {
    defaultMessage: 'Showing {count, plural, one {# result} other {# results}}',
    id: '+MUKUmHCSu',
    description:
      'Label showing how many LetterSender recipient search results are available',
  },
  loading: {
    defaultMessage: 'Loading...',
    id: 'oU7OFXYE1p',
    description: 'Loading state shown while LetterSender recipients are being fetched',
  },
  noCliniciansFound: {
    defaultMessage: 'No clinicians found',
    id: 'fCBqAgiCNf',
    description:
      'Empty state title when the LetterSender recipient search returns no matches',
  },
  noCliniciansFoundDescription: {
    defaultMessage: 'Check the spelling or try a different search.',
    id: 'bKVjWAL69S',
    description:
      'Empty state description when the LetterSender recipient search returns no matches',
  },
  noResultsMatchFilters: {
    defaultMessage: 'No results match your filters',
    id: 'ZzlJKVzhcl',
    description:
      'Empty state title when LetterSender search results exist but are excluded by active filters',
  },
  noResultsMatchFiltersDescription: {
    defaultMessage: 'Try adjusting or clearing your filters.',
    id: 't0Mv0APYj6',
    description:
      'Empty state description when active LetterSender filters exclude all search results',
  },
  startTypingToSearch: {
    defaultMessage: 'Start typing to search',
    id: 'kX93Q82WTz',
    description:
      'Prompt shown before the user enters a LetterSender recipient search query',
  },
  addAsRecipient: {
    defaultMessage: 'Add <strong>{query}</strong> as a recipient',
    id: 'HnoF43o/98',
    description: 'Action to add a typed email address as a LetterSender recipient',
  },
  addAsFaxRecipient: {
    defaultMessage: 'Add <strong>{query}</strong> as a fax recipient',
    id: 'EZYGwcPUMY',
    description: 'Action to add a typed fax number as a LetterSender recipient',
  },
  addManualRecipientIntro: {
    defaultMessage: 'Add ',
    id: 'gjZL4nXSqe',
    description:
      'Leading word ("Add") for the manual recipient dropdown button. Rendered as the first FormattedMessage around the typed value.',
  },
  addAsEmailRecipientSuffix: {
    defaultMessage: ' as a recipient',
    id: 'K4xFzCXHd2',
    description:
      'Trailing text ("as a recipient") for the manual recipient dropdown button after a typed email address.',
  },
  addAsFaxRecipientSuffix: {
    defaultMessage: ' as a fax recipient',
    id: 'JOfEzGgQje',
    description:
      'Trailing text ("as a fax recipient") for the manual recipient dropdown button after a typed fax number.',
  },
  dropzoneInstruction: {
    defaultMessage: 'Click to browse files, or drag and drop your file here.',
    id: 'HjynL9YMsv',
    description: 'Primary instruction inside the LetterSender file upload dropzone',
  },
  supportedFormats: {
    defaultMessage: 'Supported formats: {formats}',
    id: 'R14sylQfhV',
    description:
      'Supported file formats listed in the LetterSender file upload dropzone. The formats placeholder is a localized list of file format labels.',
  },
  txtFileFormat: {
    defaultMessage: 'TXT',
    id: '5KPcsuDBaD',
    description: 'Plain text file format label in the LetterSender file upload dropzone',
  },
  pdfFileFormat: {
    defaultMessage: 'PDF',
    id: 'Z/OzsrOgsJ',
    description: 'PDF file format label in the LetterSender file upload dropzone',
  },
  wordFileFormat: {
    defaultMessage: 'DOCX',
    id: 'QFzzyAS3iR',
    description:
      'Microsoft Word file format label in the LetterSender file upload dropzone',
  },
  sendToProviderTitle: {
    defaultMessage: 'Send to provider',
    id: 'LORE80DxOg',
    description: 'Title of the LetterSender send-to-recipient modal',
  },
  sendLetterTitle: {
    defaultMessage: 'Send letter',
    id: '1LA09Afbqo',
    description: 'Title of the LetterSender note panel',
  },
  sentBadge: {
    defaultMessage: 'Sent',
    id: '8rBqSDO4vF',
    description: 'Badge shown in the LetterSender note panel once a letter is sent',
  },
  toLabel: {
    defaultMessage: 'To',
    id: '5DL4YRKA9L',
    description: 'Recipient field label in the LetterSender send-to-recipient modal',
  },
  ccLabel: {
    defaultMessage: 'CC',
    id: 'R5hP7mrhnC',
    description: 'CC field label in the LetterSender send-to-recipient modal',
  },
  addCcRecipient: {
    defaultMessage: 'Add CC recipient',
    id: 'b9flm+9Ygp',
    description:
      'Aria label for the button to add a CC recipient in the LetterSender modal',
  },
  subjectLabel: {
    defaultMessage: 'Subject',
    id: '3615KnvYwY',
    description: 'Subject field label in the LetterSender note panel',
  },
  messageLabel: {
    defaultMessage: 'Message',
    id: 'zSuHaDNfoR',
    description: 'Message field label in the LetterSender note panel',
  },
  resizePanelAriaLabel: {
    defaultMessage: 'Resize letter sender panel',
    id: 'E8lAuVMitA',
    description: 'Accessible label for resizing the LetterSender note panel',
  },
  moreOptionsAriaLabel: {
    defaultMessage: 'More options',
    id: 'BHhaV3D+P3',
    description: 'Accessible label for LetterSender note panel more options',
  },
  attachFilesAriaLabel: {
    defaultMessage: 'Attach files',
    id: 'Q47LjANMxm',
    description: 'Accessible label for adding LetterSender attachments',
  },
  documentToBeSent: {
    defaultMessage: 'Document to be sent',
    id: '/9rNmI9AM8',
    description: 'Section title for the primary document in the LetterSender modal',
  },
  documentAttachmentsDescription: {
    defaultMessage: 'Click to browse, or drag and drop to attach additional attachments',
    id: 'xlbTsJ8Cwh',
    description: 'Helper text for attaching extra files in the LetterSender modal',
  },
  cancel: {
    defaultMessage: 'Cancel',
    id: 'olrmfnnZLz',
    description: 'Secondary action to close the LetterSender send-to-recipient modal',
  },
  send: {
    defaultMessage: 'Send',
    id: 'iZnQwFHx99',
    description: 'Primary action to send the LetterSender message',
  },
  sending: {
    defaultMessage: 'Sending...',
    id: 'sx14bnidQ9',
    description: 'Pending state label while the LetterSender message is being sent',
  },
  uploadingProgress: {
    defaultMessage: 'Uploading... {progress}%',
    id: '11T58dlmgF',
    description: 'Upload progress label shown for a LetterSender attachment in progress',
  },
  previewFileAriaLabel: {
    defaultMessage: 'Preview {fileName}',
    id: 'zDpSaefxh8',
    description:
      'Accessible label for the button that previews an uploaded LetterSender file',
  },
  removeFileAriaLabel: {
    defaultMessage: 'Remove {fileName}',
    id: 'yTLlt/brYn',
    description:
      'Accessible label for the button that removes an uploaded LetterSender file',
  },
  fileFallbackType: {
    defaultMessage: 'FILE',
    id: 'kg3gvWpLwH',
    description:
      'Fallback file type label when an uploaded LetterSender file type is unknown',
  },
  fallbackDocumentName: {
    defaultMessage: 'Document',
    id: 'u0TSwiR+Dr',
    description:
      'Fallback name used when the active tab does not have a title for LetterSender export',
  },
  checkValidEmailOrFax: {
    defaultMessage: 'Check you have entered a valid email or fax number',
    id: '03HzicmWEn',
    description:
      'Warning shown when the search input is not a valid email or fax number and no recipients are found',
  },
  letterSentSuccess: {
    defaultMessage: 'Letter has been sent to the provider',
    id: 'vKABhEQBq7',
    description: 'Toast message shown when a LetterSender letter is sent successfully',
  },
  letterSentError: {
    defaultMessage: 'Letter could not be sent, please try again',
    id: 'MdhTcWhZe0',
    description: 'Toast message shown when sending a LetterSender letter fails',
  },
  letterUpdateError: {
    defaultMessage: 'Letter changes could not be saved, please try again',
    id: 'cixbrmI/3J',
    description: 'Toast message shown when saving LetterSender letter changes fails',
  },
  pdfPreviewError: {
    defaultMessage: 'PDF preview could not be generated, please try again',
    id: 'ZlLsLH4eZq',
    description: 'Toast message shown when generating a LetterSender PDF preview fails',
  },
  noDocumentId: {
    defaultMessage: 'No document selected',
    id: 'ro7a3wiNwT',
    description: 'Toast message shown when trying to send without a document selected',
  },
  attachmentUploadError: {
    defaultMessage: 'Attachment upload failed, please retry',
    id: 'W+yuxkuytx',
    description:
      'Toast and inline error message shown when uploading a LetterSender attachment fails',
  },
  attachmentDeleteError: {
    defaultMessage: 'Attachment could not be removed, please try again',
    id: 'VFNLUCMude',
    description: 'Toast message shown when deleting a LetterSender attachment fails',
  },
  retryUpload: {
    defaultMessage: 'Retry',
    id: '0s/nAyD91X',
    description: 'Button label to retry a failed LetterSender attachment upload',
  },
  fileTooLarge: {
    defaultMessage: 'File exceeds {maxSize}MB limit',
    id: 'dHSAlrMftk',
    description: 'Toast message shown when a file exceeds the maximum upload size',
  },
  combinedFilesTooLarge: {
    defaultMessage: 'Files exceed {maxSize}MB combined limit',
    id: 'TS5qzAdGli',
    description:
      'Toast message shown when selected files exceed the maximum combined upload size',
  },
  fileTypeNotSupported: {
    defaultMessage: 'File type not supported',
    id: 'ohi9agKJ/l',
    description: 'Toast message shown when a file type is not accepted',
  },
  invalidEmailsError: {
    defaultMessage:
      '{count, plural, one {# email address is invalid} other {# email addresses are invalid}}',
    id: 'u+qJeiYQ99',
    description:
      'Error message shown when one or more email recipients have invalid format',
  },
  attachmentTooManyFiles: {
    defaultMessage: '{provider} allows a maximum of {maxFiles, number} attachments.',
    id: 'G56dVWUVZ7',
    description:
      'Attachment validation error shown when the selected files exceed a provider file-count limit',
  },
  attachmentCombinedFilesTooLarge: {
    defaultMessage:
      '{provider} allows a maximum combined attachment size of {maxSizeInMb, number} MB.',
    id: 'd90kmdJN40',
    description:
      'Attachment validation error shown when the selected files exceed a provider combined size limit',
  },
  attachmentFileTooLarge: {
    defaultMessage:
      '{fileName} exceeds the {provider} attachment size limit of {maxSizeInMb, number} MB.',
    id: 'Mjd8b+q5tb',
    description:
      'Attachment validation error shown when a selected file exceeds a provider per-file size limit',
  },
  attachmentFileTypeNotSupported: {
    defaultMessage: '{fileName} is not a supported attachment type for {provider}.',
    id: '4LHfhatjLs',
    description:
      'Attachment validation error shown when a selected file type is not supported by a provider',
  },
  attachmentDuplicateFileName: {
    defaultMessage:
      'A file named {fileName} is already attached. Rename it and try again.',
    id: '2D/aEt1EaZ',
    description:
      'Attachment validation error shown when a file with the same name is already attached',
  },
});
